import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import { useAuth } from '../../context/AuthContext';
import { useUpload } from '../../context/UploadContext';
import { billingAPI } from '../../services/api';
import {
  HomeIcon,
  DocumentIcon,
  UserCircleIcon,
  Bars3Icon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  ArrowRightStartOnRectangleIcon,
  MagnifyingGlassIcon,
  BellIcon,
  CreditCardIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

// Grid/Widgets icon component
const WidgetsIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
  </svg>
);

const navigation = [
  { name: 'Home', href: '/admin', icon: HomeIcon },
  { name: 'Profile', href: '/admin/profile', icon: UserCircleIcon },
  { name: 'Resources', href: '/admin/documents', icon: DocumentIcon },
  { name: 'Chat', href: '/admin/chat', icon: ChatBubbleLeftRightIcon },
  { name: 'Website Chat', href: '/admin/widgets', icon: WidgetsIcon },
  { name: 'Billing', href: '/admin/billing', icon: CreditCardIcon },
  { name: 'Settings', href: '/admin/settings', icon: Cog6ToothIcon },
];

// Notification severity styling
const severityColors = {
  info: 'bg-blue-50 text-blue-600',
  warning: 'bg-yellow-50 text-yellow-600',
  critical: 'bg-red-50 text-red-600',
};

// Notification type icons
const NotificationIcon = ({ type, className }) => {
  switch (type) {
    case 'trial_ending':
      return <ClockIcon className={className} />;
    case 'usage_exceeded':
    case 'usage_critical':
      return <ExclamationTriangleIcon className={className} />;
    case 'usage_warning':
      return <ChartBarIcon className={className} />;
    default:
      return <BellIcon className={className} />;
  }
};

export default function AdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [planName, setPlanName] = useState('Starter');
  const location = useLocation();
  const { user, logout } = useAuth();
  const { uploadingFiles } = useUpload();

  // Fetch current plan
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const data = await billingAPI.getSubscription();
        if (data?.plan) {
          setPlanName(data.plan.charAt(0).toUpperCase() + data.plan.slice(1));
        }
      } catch (error) {
        // Default to Starter if no subscription
      }
    };
    fetchPlan();
  }, []);

  // Fetch notifications on mount and periodically
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const data = await billingAPI.getNotifications();
        setNotifications(data.notifications || []);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
        setNotifications([]);
      } finally {
        setNotificationsLoading(false);
      }
    };

    fetchNotifications();

    // Refresh notifications every 5 minutes
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
  };

  // Get page title based on current route
  const getPageTitle = () => {
    const currentNav = navigation.find(item => item.href === location.pathname);
    if (currentNav) return currentNav.name === 'Home' ? 'Dashboard' : currentNav.name;
    if (location.pathname.includes('/billing')) return 'Billing';
    return 'Dashboard';
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] font-source">
      {/* Mobile sidebar overlay */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />

        {/* Mobile sidebar */}
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
          {/* Teal accent corner */}
          <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
            <div className="absolute -top-8 -right-8 w-16 h-16 bg-cleona-600 rounded-full" />
          </div>

          <div className="flex h-16 items-center justify-between px-6">
            <span className="text-2xl font-bold text-cleona-600 font-arsenal">CLEONA</span>
            <button onClick={() => setSidebarOpen(false)}>
              <XMarkIcon className="h-6 w-6 text-gray-500" />
            </button>
          </div>

          <nav className="flex-1 px-4 py-6 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href ||
                (item.href !== '/admin' && location.pathname.startsWith(item.href));
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-cleona-50 text-cleona-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <item.icon className={`mr-3 h-5 w-5 ${isActive ? 'text-cleona-600' : 'text-gray-400'}`} />
                  {item.name}
                  {item.name === 'Resources' && uploadingFiles.length > 0 && (
                    <span className="ml-auto inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 text-xs font-bold text-white bg-cleona-600 rounded-full animate-pulse">
                      {uploadingFiles.length}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="border-t border-gray-100 p-4">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <ArrowRightStartOnRectangleIcon className="mr-3 h-5 w-5 text-gray-400" />
              Log Out
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-56 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-white border-r border-gray-100 relative">
          {/* Teal accent corner */}
          <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
            <div className="absolute -top-10 -right-10 w-20 h-20 bg-cleona-600 rounded-full" />
          </div>

          <div className="flex h-16 items-center px-6 border-b border-gray-100">
            <span className="text-2xl font-bold text-cleona-600 font-arsenal">CLEONA</span>
          </div>

          <nav className="flex-1 px-3 py-6 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href ||
                (item.href !== '/admin' && location.pathname.startsWith(item.href));
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-cleona-50 text-cleona-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <item.icon className={`mr-3 h-5 w-5 ${isActive ? 'text-cleona-600' : 'text-gray-400'}`} />
                  {item.name}
                  {item.name === 'Resources' && uploadingFiles.length > 0 && (
                    <span className="ml-auto inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 text-xs font-bold text-white bg-cleona-600 rounded-full animate-pulse">
                      {uploadingFiles.length}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="border-t border-gray-100 p-3">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <ArrowRightStartOnRectangleIcon className="mr-3 h-5 w-5 text-gray-400" />
              Log Out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-56">
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex h-16 items-center gap-x-4 bg-white px-4 lg:px-6 border-b border-gray-100">
          {/* Mobile menu button */}
          <button
            type="button"
            className="lg:hidden -m-2.5 p-2.5 text-gray-700"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* Page title */}
          <h1 className="text-lg font-semibold text-gray-900 font-arsenal">
            {getPageTitle()}
          </h1>

          {/* Search bar */}
          <div className="flex-1 max-w-md mx-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search anything..."
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent font-source"
              />
            </div>
          </div>

          {/* Right side items */}
          <div className="flex items-center gap-x-3 ml-auto">
            {/* Plan badge */}
            <Link to="/admin/billing" className="flex items-center gap-1.5 px-3 py-1.5 bg-cleona-600 text-white text-xs font-medium rounded-lg hover:bg-cleona-700 transition-colors">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {planName}
            </Link>

            {/* Notifications */}
            <Menu as="div" className="relative">
              <Menu.Button className="relative p-2 text-gray-500 hover:text-gray-700 transition-colors rounded-lg hover:bg-gray-50">
                <BellIcon className="h-5 w-5" />
                {notifications.length > 0 && (
                  <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-cleona-500 rounded-full" />
                )}
              </Menu.Button>

              <Menu.Items className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50 focus:outline-none">
                <div className="p-3 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-900">Notifications</h3>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {notificationsLoading ? (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      Loading...
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      No notifications
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <Menu.Item key={notification.id}>
                        {({ active }) => (
                          <Link
                            to={notification.action_url || '/admin/billing'}
                            className={`block p-3 border-b border-gray-50 last:border-b-0 ${
                              active ? 'bg-gray-50' : ''
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-lg ${severityColors[notification.severity]}`}>
                                <NotificationIcon type={notification.type} className="h-4 w-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{notification.message}</p>
                                {notification.action_label && (
                                  <span className="inline-block mt-1.5 text-xs font-medium text-cleona-600 hover:text-cleona-700">
                                    {notification.action_label} →
                                  </span>
                                )}
                              </div>
                            </div>
                          </Link>
                        )}
                      </Menu.Item>
                    ))
                  )}
                </div>
              </Menu.Items>
            </Menu>

            {/* User avatar */}
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                {user?.avatar ? (
                  <img src={user.avatar} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-sm font-medium text-gray-600">
                    {user?.username?.[0]?.toUpperCase() || 'U'}
                  </span>
                )}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-gray-900">{user?.username || 'User'}</p>
                <p className="text-xs text-gray-500">Admin</p>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
